// src/hooks/useCotizaciones.js
import { useState, useEffect } from "react";
import {
  collection, addDoc, updateDoc, deleteDoc,
  doc, onSnapshot, query, orderBy, where,
  serverTimestamp, getDoc
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { useRol } from "./useRoles";
import toast from "react-hot-toast";

const COL     = "cotizaciones";
const COL_HIS = "historial";
// Aprobada = cerrada ganada en buyer (Pendiente → Aprobada → Pedido). Ganada/Cerrada = legado CRM manual.
const ESTADOS_TERMINALES = ["Ganada", "Cerrada", "Aprobada", "Perdida", "Rechazada", "Vencida"];
// "Vencida" no tuvo respuesta definitiva — a diferencia de Ganada/Perdida/Rechazada, no debe
// borrar el seguimiento de la cotización (el vendedor puede querer agendar un recontacto
// para intentar revivirla, y nunca pasa por el modal que mueve el seguimiento al cliente).
const ESTADOS_QUE_LIMPIAN_SEGUIMIENTO = ESTADOS_TERMINALES.filter(e => e !== "Vencida");

export function useCotizaciones() {
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const { rol, perfil, permisos }       = useRol();

  useEffect(() => {
    if (!rol) return;
    let q;
    if (permisos.verTodosClientes) {
      q = query(collection(db, COL), orderBy("creadoEn", "desc"));
    } else {
      const email = auth.currentUser?.email || "";
      q = query(
        collection(db, COL),
        where("responsable", "==", perfil?.nombre || email),
        orderBy("creadoEn", "desc")
      );
    }
    const unsub = onSnapshot(q, snap => {
      setCotizaciones(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Error cotizaciones:", error);
      setCotizaciones([]);
      setLoading(false);
    });
    const timeout = setTimeout(() => setLoading(false), 5000);
    return () => { unsub(); clearTimeout(timeout); };
  }, [rol, permisos, perfil]);

  const crearCotizacion = async (datos) => {
    try {
      await addDoc(collection(db, COL), {
        ...datos,
        estado: datos.estado || "Nueva",
        creadoEn: serverTimestamp(),
        actualizadoEn: serverTimestamp(),
      });
      toast.success("✅ Cotización creada correctamente");
    } catch (e) {
      toast.error("Error al crear cotización");
      console.error(e);
    }
  };

  const actualizarCotizacion = async (id, datos) => {
    try {
      const snapAnterior = await getDoc(doc(db, COL, id));
      const estadoAnterior = snapAnterior.exists() ? snapAnterior.data().estado : null;
      const estadoNuevo    = datos.estado;

      const estadoCambio      = estadoNuevo && estadoAnterior !== estadoNuevo;
      const pasa_a_terminal   = estadoCambio && ESTADOS_QUE_LIMPIAN_SEGUIMIENTO.includes(estadoNuevo) && !ESTADOS_TERMINALES.includes(estadoAnterior);

      const updates = {
        ...datos,
        actualizadoEn: serverTimestamp(),
        ...(estadoCambio      ? { estadoCambiadoEn:    serverTimestamp() } : {}),
        ...(pasa_a_terminal   ? { proximoSeguimiento: ""               } : {}),
      };
      await updateDoc(doc(db, COL, id), updates);

      // Guardar en historial si cambia a Ganada/Cerrada o Perdida
      if (
        estadoNuevo &&
        estadoAnterior !== estadoNuevo &&
        (["Ganada", "Cerrada", "Aprobada", "Perdida"].includes(estadoNuevo))
      ) {
        const d = snapAnterior.data();
        await addDoc(collection(db, COL_HIS), {
          cotizacionId:  id,
          numeroCot:     d.numeroCot     || "",
          clienteId:     d.clienteId     || "",
          nombre:        d.clienteNombre || "",
          empresa:       d.clienteEmpresa|| "",
          proyecto:      d.proyecto      || "",
          responsable:   d.responsable   || "",
          valor:         Number(datos.valor || d.valor) || 0,
          estadoAnterior,
          estadoFinal:   estadoNuevo,
          comentario:    datos.comentarioHistorial || "",
          fecha:         serverTimestamp(),
          creadoPor:     auth.currentUser?.email || "",
        });
      }

      toast.success("✅ Cotización actualizada");
    } catch (e) {
      toast.error("Error al actualizar");
      console.error(e);
    }
  };

  const eliminarCotizacion = async (id) => {
    if (!permisos.puedeEliminar) {
      toast.error("No tienes permiso para eliminar");
      return;
    }
    try {
      await deleteDoc(doc(db, COL, id));
      toast.success("🗑 Cotización eliminada");
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  // KPIs calculados sobre cotizaciones
  // "Cerrada" se mantiene por compatibilidad con cotizaciones anteriores al sync buyer
  const ESTADOS_GANADA   = ["Ganada", "Cerrada", "Aprobada"];
  const ESTADOS_CERRADOS = ["Ganada", "Cerrada", "Aprobada", "Perdida", "Rechazada", "Vencida"];

  const kpis = {
    total:        cotizaciones.length,
    activas:      cotizaciones.filter(c => !ESTADOS_CERRADOS.includes(c.estado)).length,
    cerradas:     cotizaciones.filter(c => ESTADOS_GANADA.includes(c.estado)).length,
    perdidas:     cotizaciones.filter(c => c.estado === "Perdida").length,
    valorCerrado: cotizaciones.filter(c => ESTADOS_GANADA.includes(c.estado)).reduce((s,c) => s+(Number(c.valor)||0), 0),
    valorPerdido: cotizaciones.filter(c => c.estado === "Perdida").reduce((s,c) => s+(Number(c.valor)||0), 0),
    pipeline:     cotizaciones.filter(c => !ESTADOS_CERRADOS.includes(c.estado)).reduce((s,c) => s+(Number(c.valor)||0), 0),
    negociacion:  cotizaciones.filter(c => c.estado === "Aprobada").length,
    conversion:   cotizaciones.length > 0
      ? ((cotizaciones.filter(c => ESTADOS_GANADA.includes(c.estado)).length / cotizaciones.length) * 100).toFixed(1)
      : 0,
    vencidas: cotizaciones.filter(c => {
      if (!c.proximoSeguimiento || ESTADOS_CERRADOS.includes(c.estado)) return false;
      const [y,m,d]=String(c.proximoSeguimiento).split("-").map(Number);
      const hoy=new Date(); hoy.setHours(0,0,0,0);
      return new Date(y,m-1,d) < hoy;
    }).length,
  };

  return { cotizaciones, loading, kpis, crearCotizacion, actualizarCotizacion, eliminarCotizacion };
}
